import React, { useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { useHistory } from "react-router-dom";

const sectionTitleStyle = {
  fontFamily: "Poppins, sans-serif",
  fontWeight: 700,
  fontSize: "20px",
  color: "#142C44",
  marginBottom: "12px",
};

const subTextStyle = {
  fontFamily: "Manrope, sans-serif",
  fontWeight: 600,
  fontSize: "12px",
  color: "rgba(0,0,0,0.45)",
  margin: 0,
};

const valueTextStyle = {
  fontFamily: "Manrope, sans-serif",
  fontWeight: 800,
  fontSize: "16px",
  color: "rgba(0,0,0,0.65)",
  margin: "2px 0 12px 0",
};

const controlSelectStyle = {
  borderRadius: "10px",
  border: "1px solid rgba(14, 90, 165, 0.2)",
  height: "36px",
  padding: "0 12px",
  fontFamily: "Manrope, sans-serif",
  fontSize: "13px",
  color: "#142C44",
  backgroundColor: "#fff",
};

const formatCurrency = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "-";
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "SGD",
    maximumFractionDigits: 0,
  });
};

const formatRate = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "-";
  return `${(num * 100).toFixed(1)}%`;
};

const getTimestamp = (dateString) => {
  if (!dateString) return 0;
  const d = new Date(dateString);
  const timestamp = d.getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const SolePropCard = ({ loan, onToggleMarket }) => (
  <div
    style={{
      background: "#ffffffb3",
      borderRadius: "12px",
      padding: "16px",
      minHeight: "295px",
      border: "1px solid rgba(14,90,165,0.08)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      alignItems: "flex-start",
    }}
  >
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "8px",
        }}
      >
        <div
          style={{
            fontFamily: "Poppins, sans-serif",
            fontWeight: 700,
            fontSize: "16px",
            color: "#142C44CC",
            margin: 0,
          }}
        >
          {loan.principal_name || "-"}
        </div>
        <span
          style={{
            background: "#EAF3FF",
            color: "#0E5AA5",
            borderRadius: "999px",
            padding: "4px 10px",
            fontFamily: "Manrope, sans-serif",
            fontWeight: 700,
            fontSize: "12px",
            whiteSpace: "nowrap",
            height: "fit-content",
          }}
        >
          Corporate Loan
        </span>
      </div>

      <p style={subTextStyle}>Loan ID</p>
      <p style={valueTextStyle}>{loan.loan_id || "-"}</p>

      <p style={subTextStyle}>Requested Amount</p>
      <p style={valueTextStyle}>{formatCurrency(loan.request_amount)}</p>

      <p style={subTextStyle}>Preferred Rate</p>
      <p style={valueTextStyle}>{formatRate(loan.request_interest_rate)}</p>

      <p style={subTextStyle}>Preferred Tenor</p>
      <p style={valueTextStyle}>
        {loan.request_period ? `${loan.request_period} Months` : "-"}
      </p>
    </div>

    <div style={{ width: "100%" }}>
      <button
        onClick={() => onToggleMarket(loan.loan_id, !loan.visibility)}
        style={{
          marginTop: "14px",
          width: "100%",
          border: loan.visibility
            ? "2px solid rgba(176, 79, 35, 0.35)"
            : "2px solid rgba(14, 90, 165, 0.2)",
          borderRadius: "14px",
          padding: "6px 10px",
          fontFamily: "Manrope, sans-serif",
          fontSize: "12px",
          fontWeight: 700,
          color: loan.visibility ? "#9a4b1f" : "#0e5aa5",
          background: loan.visibility ? "#fff4ed" : "#f5fbff",
          cursor: "pointer",
        }}
      >
        {loan.visibility ? "Remove from Market" : "Approve to Market"}
      </button>
    </div>
  </div>
);

const SolePropStruvtureLoan = () => {
  const history = useHistory();
  const [sortBy, setSortBy] = useState("default");
  const [loans, setLoans] = useState([]);

  useEffect(() => {
    const isAuthenticated = Cookies.get("token");
    const userAccountType = Cookies.get("user_account_type");

    if (!isAuthenticated) {
      history.push("/auth/signin");
    } else if (userAccountType === "lender") {
      history.push("/dashboard");
    } else if (userAccountType === "borrower") {
      history.push("/b-dashboard");
    }
  }, [history]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          "https://www.smart-lend.com/api/loan/viewLoanRequest",
          {
            headers: {
              Authorization: "Bearer " + Cookies.get("token"),
              Accept: "application/json",
            },
          },
        );
        const json = await response.json();
        const flatData = json["Requested Loan"] || [];
        setLoans(flatData.filter((loan) => loan.sole_prop === 1));
      } catch (error) {
        console.error(error);
      }
    };
    fetchData();
  }, []);

  const sortedLoans = useMemo(() => {
    const items = [...loans];

    if (sortBy === "loan-amount") {
      items.sort((a, b) => Number(b.request_amount) - Number(a.request_amount));
      return items;
    }

    if (sortBy === "date") {
      items.sort(
        (a, b) => getTimestamp(b.created_at) - getTimestamp(a.created_at),
      );
      return items;
    }

    items.sort((a, b) => getTimestamp(b.created_at) - getTimestamp(a.created_at));
    return items;
  }, [loans, sortBy]);

  const toggleMarket = async (loanId, visibility) => {
    try {
      const response = await fetch(
        `https://www.smart-lend.com/api/loan/visibleLoanRequest?loan_id=${loanId}&visibility=${visibility ? 1 : 0}`,
        {
          method: "PUT",
          headers: {
            Authorization: "Bearer " + Cookies.get("token"),
            Accept: "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Unable to update loan visibility.");
      }

      setLoans((current) =>
        current.map((loan) =>
          loan.loan_id === loanId ? { ...loan, visibility } : loan,
        ),
      );
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div
      style={{
        margin: "20px",
        maxHeight: "calc(100vh - 130px)",
        overflowY: "auto",
      }}
    >
      <h2 style={sectionTitleStyle}>Sole Prop Loan Request</h2>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "12px",
          flexWrap: "wrap",
          marginBottom: "14px",
        }}
      >
        <p
          style={{
            ...subTextStyle,
            fontSize: "13px",
          }}
        >
          Review sole prop loans and decide whether to place them in the
          marketplace.
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span
            style={{
              fontFamily: "Manrope, sans-serif",
              fontSize: "12px",
              fontWeight: 700,
              color: "#435669",
            }}
          >
            Sort by
          </span>
          <select
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value)}
            style={controlSelectStyle}
          >
            <option value="default">Date & Time</option>
            <option value="loan-amount">Loan Amount</option>
            <option value="date">Date</option>
          </select>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: "14px",
          paddingBottom: "10px",
        }}
      >
        {sortedLoans.map((loan) => (
          <SolePropCard
            key={loan.loan_id}
            loan={loan}
            onToggleMarket={toggleMarket}
          />
        ))}
      </div>
    </div>
  );
};

export default SolePropStruvtureLoan;
